import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      badge: "ENTERPRISE SOLUTIONS",
      title: "Data Center Excellence",
      subtitle: "",
      description: "Enterprise-grade data center services from construction to operational support with 24/7 monitoring, support, and world-class infrastructure for your business needs.",
      bgClass: "from-slate-800 via-indigo-900 to-blue-900"
    },
    {
      badge: "GLOBAL REACH",
      title: "Your Trusted Telecommunication Partner",
      subtitle: "ENHANCING GLOBAL CONNECTIONS",
      description: "We provide cutting-edge telecommunications solutions that connect businesses and communities worldwide with reliable, high-speed connectivity.",
      bgClass: "from-slate-900 via-blue-900 to-slate-800"
    },
    {
      badge: "CONNECTING BEYOND BOUNDARIES",
      title: "Connecting Your World Seamlessly",
      subtitle: "",
      description: "Experience seamless connectivity across continents with our advanced network infrastructure and innovative telecommunications solutions.",
      bgClass: "from-blue-900 via-cyan-900 to-slate-900"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <section id="home" className="relative min-h-screen overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-all duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
        >
          {/* Background Gradient */}
          <div className={`absolute inset-0 bg-gradient-to-br ${slide.bgClass}`}></div>

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
            <div className="absolute bottom-20 left-20 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-pulse"></div>
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>

            {/* Floating geometric shapes */}
            <div className="absolute top-32 right-32 w-20 h-20 border border-white/10 rounded-lg transform rotate-45 animate-float"></div>
            <div className="absolute bottom-40 right-40 w-12 h-12 border border-cyan-400/20 rounded-full animate-float-delayed"></div>
            <div className="absolute top-1/3 left-1/4 w-8 h-8 bg-blue-400/10 rounded-lg transform rotate-12 animate-pulse"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 flex items-center min-h-screen pt-24 pb-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto w-full">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className={`transform transition-all duration-1000 delay-300 ${index === currentSlide ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                  }`}>
                  {/* Badge */}
                  <div className="inline-flex items-center mb-6 px-5 py-2.5 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-cyan-400/30 rounded-full backdrop-blur-sm">
                    <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 animate-pulse"></span>
                    <span className="text-cyan-300 text-sm font-semibold tracking-wider">{slide.badge}</span>
                  </div>

                  {/* Subtitle */}
                  {slide.subtitle && (
                    <p className="text-cyan-400 text-lg font-medium mb-2 tracking-wide">{slide.subtitle}</p>
                  )}

                  {/* Title */}
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">
                    {slide.title.split(' ').map((word, i) => (
                      <span key={i}>
                        {i >= slide.title.split(' ').length - 1 ? (
                          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{word}</span>
                        ) : (
                          word + ' '
                        )}
                      </span>
                    ))}
                  </h1>

                  {/* Description */}
                  <p className="text-lg text-gray-300 mb-10 leading-relaxed max-w-xl">
                    {slide.description}
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-5 mt-4">
                    <Link
                      to="/contact"
                      className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-cyan-500/30 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                    >
                      Get Started
                      <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                      to="/services"
                      className="px-8 py-4 border-2 border-white/30 hover:border-cyan-400 text-white rounded-xl font-semibold hover:bg-white/5 transition-all duration-300 flex items-center justify-center gap-2 backdrop-blur-sm"
                    >
                      Our Services
                    </Link>
                  </div>
                </div>

                {/* Right Side Visual - Realistic Business Images */}
                <div className="hidden lg:flex items-center justify-center">
                  <div className="relative">
                    {/* Main Image Container with Decorative Frame */}
                    <div className="w-[420px] h-[380px] relative group">
                      {/* Background decorative elements */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-3xl transform rotate-3 backdrop-blur-xl border border-white/10"></div>
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/10 to-blue-400/10 rounded-3xl transform -rotate-2 backdrop-blur-xl border border-white/10"></div>

                      {/* Main Image */}
                      <div className="absolute inset-2 rounded-2xl overflow-hidden shadow-2xl shadow-blue-500/20 border border-white/20">
                        <img
                          src={index === 0 ? "/images/global_network_hero.png" : index === 1 ? "/images/data_center_hero.png" : "/images/it_team_hero.png"}
                          alt={index === 0 ? "Global Network Infrastructure" : index === 1 ? "Data Center Operations" : "IT Team Excellence"}
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                        />
                        {/* Overlay gradient for depth */}
                        <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 via-transparent to-transparent"></div>
                      </div>

                      {/* Floating decorative dots */}
                      <div className="absolute -top-3 -right-3 w-6 h-6 bg-cyan-400 rounded-full animate-pulse"></div>
                      <div className="absolute -bottom-3 -left-3 w-4 h-4 bg-blue-500 rounded-full animate-pulse delay-75"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 border border-white/20"
      >
        <FaChevronLeft size={18} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 border border-white/20"
      >
        <FaChevronRight size={18} />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide
              ? 'w-8 bg-gradient-to-r from-cyan-400 to-blue-400'
              : 'w-2 bg-white/30 hover:bg-white/50'
              }`}
          />
        ))}
      </div>

      {/* Clean bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent z-10"></div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(45deg); }
          50% { transform: translateY(-20px) rotate(45deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Hero;
